import { z } from 'zod';
import { PROCEDURE_AREAS } from './crm.js';
// ------------------------------------------------------------ paciente
const cpfDigits = z.string().trim().transform((s) => s.replace(/\D/g, '')).pipe(z.string().length(11, 'CPF precisa de 11 dígitos'));
const isoDate = z.iso.date('Data inválida');
export const SEX_AT_BIRTH = ['female', 'male', 'intersex', 'not_informed'];
export const addressSchema = z.object({
    zip: z.string().trim().max(9).optional(),
    street: z.string().trim().max(160).optional(),
    city: z.string().trim().max(80).optional(),
    state: z.string().trim().max(2).optional(),
});
export const emergencyContactSchema = z.object({
    name: z.string().trim().max(80),
    phone: z.string().trim().max(20),
    relation: z.string().trim().max(40).optional(),
});
export const patientFieldsSchema = z.object({
    cpf: cpfDigits.nullable().optional(),
    birthDate: isoDate.nullable().optional(),
    sexAtBirth: z.enum(SEX_AT_BIRTH).nullable().optional(),
    address: addressSchema.nullable().optional(),
    emergencyContact: emergencyContactSchema.nullable().optional(),
});
/** Abre o prontuário de um contato que já existe, ou cria o contato junto (nome + celular). */
export const createPatientSchema = patientFieldsSchema.extend({
    contactId: z.uuid().optional(),
    name: z.string().trim().min(2, 'Informe o nome').max(120).optional(),
    phone: z.string().trim().max(20).optional(),
}).refine((v) => v.contactId || (v.name && v.phone), { message: 'Escolha um contato ou informe nome e celular', path: ['name'] });
export const patientSummarySchema = z.object({
    id: z.uuid(),
    contactId: z.uuid(),
    name: z.string(),
    pseudonym: z.string(),
    phoneLast4: z.string().nullable(),
    age: z.number().int().nullable(),
    redFlags: z.array(z.string()),
    lastEvaluation: z.object({ status: z.enum(['draft', 'signed']), classification: z.string().nullable(), at: z.string() }).nullable(),
    createdAt: z.string(),
});
export const patientSchema = patientSummarySchema.extend({
    cpf: z.string().nullable(),
    birthDate: z.string().nullable(),
    sexAtBirth: z.enum(SEX_AT_BIRTH).nullable(),
    address: addressSchema.nullable(),
    emergencyContact: emergencyContactSchema.nullable(),
});
// ------------------------------------------------------------ anamnese
export const CHRONIC_CONDITIONS = ['diabetes', 'hypertension', 'cardiac', 'autoimmune', 'thyroid', 'infectious', 'keloid', 'bleeding_disorder'];
export const HAIR_TREATMENTS = ['minoxidil', 'finasteride', 'dutasteride', 'prp', 'laser', 'other'];
const yesNo = z.boolean().nullable().default(null);
export const anamnesisAnswersSchema = z.object({
    chiefComplaint: z.string().trim().max(2000).default(''),
    hairLossOnsetYears: z.number().int().min(0).max(80).nullable().default(null),
    familyHistory: yesNo,
    previousTransplant: yesNo,
    treatments: z.array(z.enum(HAIR_TREATMENTS)).default([]),
    chronicConditions: z.array(z.enum(CHRONIC_CONDITIONS)).default([]),
    medications: z.string().trim().max(2000).default(''),
    anticoagulants: yesNo,
    allergies: z.string().trim().max(1000).default(''),
    anestheticAllergy: yesNo,
    smoker: yesNo,
    pregnantOrBreastfeeding: yesNo,
    /** preocupação desproporcional com a aparência (triagem de dismorfia corporal) */
    appearanceDistress: yesNo,
    expectations: z.string().trim().max(2000).default(''),
    notes: z.string().trim().max(4000).default(''),
});
export const RED_FLAGS = {
    anticoagulant: 'Usa anticoagulante',
    bleeding_disorder: 'Distúrbio de coagulação',
    keloid: 'Histórico de queloide',
    anesthetic_allergy: 'Alergia a anestésico',
    cardiac: 'Doença cardíaca',
    diabetes: 'Diabetes',
    infectious: 'Doença infectocontagiosa',
    pregnancy: 'Gestante ou amamentando',
    body_dysmorphia: 'Triagem de dismorfia corporal',
    smoker: 'Tabagista',
};
/** Sinais de alerta calculados das respostas (o servidor recalcula; a tela usa para mostrar ao vivo). */
export function anamnesisRedFlags(a) {
    const f = [];
    if (a.anticoagulants)
        f.push('anticoagulant');
    for (const c of ['bleeding_disorder', 'keloid', 'cardiac', 'diabetes', 'infectious'])
        if (a.chronicConditions.includes(c))
            f.push(c);
    if (a.anestheticAllergy)
        f.push('anesthetic_allergy');
    if (a.pregnantOrBreastfeeding)
        f.push('pregnancy');
    if (a.appearanceDistress)
        f.push('body_dysmorphia');
    if (a.smoker)
        f.push('smoker');
    return f;
}
export const anamnesisSchema = z.object({
    id: z.uuid(),
    patientId: z.uuid(),
    status: z.enum(['draft', 'signed']),
    answers: anamnesisAnswersSchema,
    redFlags: z.array(z.string()),
    createdByName: z.string().nullable(),
    signedByName: z.string().nullable(),
    signedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    addenda: z.array(z.object({ id: z.uuid(), text: z.string(), authorName: z.string(), createdAt: z.string() })),
});
// ------------------------------------------------------------ avaliação capilar
export const CLASSIFICATIONS = {
    norwood_hamilton: ['I', 'II', 'IIa', 'III', 'IIIa', 'III vertex', 'IV', 'IVa', 'V', 'Va', 'VI', 'VII'],
    ludwig: ['I', 'II', 'III'],
    savin: ['I-1', 'I-2', 'I-3', 'I-4', 'II-1', 'II-2', 'III', 'avançado', 'frontal'],
    none: [],
};
export const TECHNIQUES = ['FUE', 'FUT', 'DHI', 'other'];
export const INDICATIONS = ['indicated', 'indicated_with_caveats', 'not_indicated'];
export const donorAreaSchema = z.object({
    /** unidades foliculares por cm² */
    densityPerCm2: z.number().int().min(0).max(200).nullable().default(null),
    caliber: z.enum(['thin', 'medium', 'thick']).nullable().default(null),
    hairsPerUnit: z.number().min(1).max(5).nullable().default(null),
    quality: z.enum(['poor', 'fair', 'good', 'excellent']).nullable().default(null),
    sufficient: z.boolean().nullable().default(null),
});
export const evaluationFieldsSchema = z.object({
    procedureArea: z.enum(PROCEDURE_AREAS).default('scalp'),
    classificationSystem: z.enum(Object.keys(CLASSIFICATIONS)).default('norwood_hamilton'),
    classificationGrade: z.string().trim().max(20).nullable().default(null),
    donorArea: donorAreaSchema.default({ densityPerCm2: null, caliber: null, hairsPerUnit: null, quality: null, sufficient: null }),
    graftsEstimateMin: z.number().int().min(0).max(10000).nullable().default(null),
    graftsEstimateMax: z.number().int().min(0).max(10000).nullable().default(null),
    technique: z.enum(TECHNIQUES).nullable().default(null),
    indication: z.enum(INDICATIONS).nullable().default(null),
    notes: z.string().trim().max(8000).default(''),
    appointmentId: z.uuid().nullable().default(null),
}).refine((v) => v.graftsEstimateMin == null || v.graftsEstimateMax == null || v.graftsEstimateMin <= v.graftsEstimateMax, {
    message: 'O mínimo de enxertos não pode passar do máximo', path: ['graftsEstimateMax'],
}).refine((v) => !v.classificationGrade || v.classificationSystem === 'none'
    || CLASSIFICATIONS[v.classificationSystem].includes(v.classificationGrade), {
    message: 'Grau não existe nessa escala', path: ['classificationGrade'],
});
export const evaluationSchema = z.object({
    id: z.uuid(),
    patientId: z.uuid(),
    status: z.enum(['draft', 'signed']),
    fields: evaluationFieldsSchema,
    doctorName: z.string(),
    signedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    addenda: z.array(z.object({ id: z.uuid(), text: z.string(), authorName: z.string(), createdAt: z.string() })),
});
export const addendumInputSchema = z.object({ text: z.string().trim().min(3, 'Escreva o adendo').max(4000) });
// ------------------------------------------------------------ linha do tempo
export const timelineItemSchema = z.object({
    at: z.string(),
    kind: z.enum(['patient_created', 'anamnesis', 'evaluation', 'addendum', 'appointment']),
    title: z.string(),
    detail: z.string().nullable(),
    refId: z.uuid().nullable(),
    status: z.string().nullable(),
});
//# sourceMappingURL=clinical.js.map